import motionBlurWGSL from "./shaders/motion-blur.wgsl?raw";

const HDR_FORMAT: GPUTextureFormat = "rgba16float";
const VEL_FORMAT: GPUTextureFormat = "rg16float";

/** Keep aligned with TILE_SIZE in motion-blur.wgsl. */
const TILE_SIZE = 20;

const PARAMS_BYTES = 32;

export class MotionBlurPass {
  private device: GPUDevice;
  private sampler: GPUSampler;

  private maxLayout: GPUBindGroupLayout;
  private reconLayout: GPUBindGroupLayout;

  private tileMaxPipeline: GPURenderPipeline;
  private neighborMaxPipeline: GPURenderPipeline;
  private reconstructPipeline: GPURenderPipeline;

  private velTex: GPUTexture | null = null;
  private velView: GPUTextureView | null = null;
  private tileMaxTex: GPUTexture | null = null;
  private tileMaxView: GPUTextureView | null = null;
  private nbrMaxTex: GPUTexture | null = null;
  private nbrMaxView: GPUTextureView | null = null;
  private outputTex: GPUTexture | null = null;
  private outView: GPUTextureView | null = null;

  /** One UBO per pass; shared buffer would only carry final write. */
  private tileMaxUBO: GPUBuffer;
  private nbrMaxUBO: GPUBuffer;
  private reconUBO: GPUBuffer;

  private tileMaxBind: GPUBindGroup | null = null;
  private nbrMaxBind: GPUBindGroup | null = null;
  private reconBind: GPUBindGroup | null = null;

  private srcW = 0;
  private srcH = 0;
  private tileW = 0;
  private tileH = 0;

  private scratch = new Float32Array(PARAMS_BYTES / 4);

  constructor(device: GPUDevice) {
    this.device = device;

    this.sampler = device.createSampler({
      label: "motion-blur sampler",
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    });

    this.maxLayout = device.createBindGroupLayout({
      label: "motion-blur max bgl",
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
      ],
    });
    this.reconLayout = device.createBindGroupLayout({
      label: "motion-blur recon bgl",
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: "uniform" } },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
        { binding: 4, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: "float" } },
      ],
    });

    const shader = device.createShaderModule({
      label: "motion-blur",
      code: motionBlurWGSL,
    });

    this.tileMaxPipeline = device.createRenderPipeline({
      label: "motion-blur tile-max",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.maxLayout] }),
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_tile_max",
        targets: [{ format: VEL_FORMAT }],
      },
      primitive: { topology: "triangle-list" },
    });
    this.neighborMaxPipeline = device.createRenderPipeline({
      label: "motion-blur neighbor-max",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.maxLayout] }),
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_neighbor_max",
        targets: [{ format: VEL_FORMAT }],
      },
      primitive: { topology: "triangle-list" },
    });
    this.reconstructPipeline = device.createRenderPipeline({
      label: "motion-blur reconstruct",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.reconLayout] }),
      vertex: { module: shader, entryPoint: "vs_main" },
      fragment: {
        module: shader,
        entryPoint: "fs_reconstruct",
        targets: [{ format: HDR_FORMAT }],
      },
      primitive: { topology: "triangle-list" },
    });

    this.tileMaxUBO = this.makeUBO("motion-blur tile-max ubo");
    this.nbrMaxUBO = this.makeUBO("motion-blur neighbor-max ubo");
    this.reconUBO = this.makeUBO("motion-blur recon ubo");
  }

  private makeUBO(label: string): GPUBuffer {
    return this.device.createBuffer({
      label,
      size: PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });
  }

  /** View written by the scene render pass as @location(1). */
  velocityView(): GPUTextureView {
    if (!this.velView) throw new Error("MotionBlurPass.velocityView() called before resize()");
    return this.velView;
  }

  /** Downstream passes read this instead of raw HDR. */
  outputView(): GPUTextureView {
    if (!this.outView) throw new Error("MotionBlurPass.outputView() called before resize()");
    return this.outView;
  }

  /** Call after every HDR recreate; hdrView feeds the reconstruct bind. */
  resize(srcW: number, srcH: number, hdrView: GPUTextureView): void {
    this.srcW = srcW;
    this.srcH = srcH;
    this.tileW = Math.max(1, Math.ceil(srcW / TILE_SIZE));
    this.tileH = Math.max(1, Math.ceil(srcH / TILE_SIZE));

    for (const t of [this.velTex, this.tileMaxTex, this.nbrMaxTex, this.outputTex]) {
      if (t) t.destroy();
    }

    this.velTex = this.device.createTexture({
      label: "motion-blur velocity",
      size: { width: srcW, height: srcH },
      format: VEL_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.velView = this.velTex.createView();

    this.tileMaxTex = this.device.createTexture({
      label: "motion-blur tile-max",
      size: { width: this.tileW, height: this.tileH },
      format: VEL_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.tileMaxView = this.tileMaxTex.createView();

    this.nbrMaxTex = this.device.createTexture({
      label: "motion-blur neighbor-max",
      size: { width: this.tileW, height: this.tileH },
      format: VEL_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.nbrMaxView = this.nbrMaxTex.createView();

    this.outputTex = this.device.createTexture({
      label: "motion-blur output",
      size: { width: srcW, height: srcH },
      format: HDR_FORMAT,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    this.outView = this.outputTex.createView();

    this.tileMaxBind = this.device.createBindGroup({
      label: "motion-blur tile-max bind",
      layout: this.maxLayout,
      entries: [
        { binding: 0, resource: { buffer: this.tileMaxUBO } },
        { binding: 1, resource: this.velView },
        { binding: 2, resource: this.sampler },
      ],
    });
    this.nbrMaxBind = this.device.createBindGroup({
      label: "motion-blur neighbor-max bind",
      layout: this.maxLayout,
      entries: [
        { binding: 0, resource: { buffer: this.nbrMaxUBO } },
        { binding: 1, resource: this.tileMaxView },
        { binding: 2, resource: this.sampler },
      ],
    });
    this.reconBind = this.device.createBindGroup({
      label: "motion-blur recon bind",
      layout: this.reconLayout,
      entries: [
        { binding: 0, resource: { buffer: this.reconUBO } },
        { binding: 1, resource: this.velView },
        { binding: 2, resource: this.sampler },
        { binding: 3, resource: hdrView },
        { binding: 4, resource: this.nbrMaxView },
      ],
    });
  }

  encode(enc: GPUCommandEncoder, blurAmount: number): void {
    if (!this.tileMaxBind || !this.nbrMaxBind || !this.reconBind) return;

    const q = this.device.queue;
    const s = this.scratch;
    s.fill(0);

    s[0] = this.srcW; s[1] = this.srcH;
    s[2] = this.tileW; s[3] = this.tileH;
    q.writeBuffer(this.tileMaxUBO, 0, s.buffer, 0, PARAMS_BYTES);

    s[0] = this.tileW; s[1] = this.tileH;
    s[2] = this.tileW; s[3] = this.tileH;
    q.writeBuffer(this.nbrMaxUBO, 0, s.buffer, 0, PARAMS_BYTES);

    s[0] = this.srcW; s[1] = this.srcH;
    s[2] = this.srcW; s[3] = this.srcH;
    s[4] = blurAmount;
    q.writeBuffer(this.reconUBO, 0, s.buffer, 0, PARAMS_BYTES);

    {
      const pass = enc.beginRenderPass({
        label: "motion-blur tile-max",
        colorAttachments: [{
          view: this.tileMaxView!,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      });
      pass.setPipeline(this.tileMaxPipeline);
      pass.setBindGroup(0, this.tileMaxBind);
      pass.draw(3);
      pass.end();
    }

    {
      const pass = enc.beginRenderPass({
        label: "motion-blur neighbor-max",
        colorAttachments: [{
          view: this.nbrMaxView!,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      });
      pass.setPipeline(this.neighborMaxPipeline);
      pass.setBindGroup(0, this.nbrMaxBind);
      pass.draw(3);
      pass.end();
    }

    {
      const pass = enc.beginRenderPass({
        label: "motion-blur reconstruct",
        colorAttachments: [{
          view: this.outView!,
          loadOp: "clear",
          storeOp: "store",
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
        }],
      });
      pass.setPipeline(this.reconstructPipeline);
      pass.setBindGroup(0, this.reconBind);
      pass.draw(3);
      pass.end();
    }
  }
}
